import {
  useFileRecordVersionsQuery,
  useRestoreFileRecordVersionMutation,
} from "@/api/fileRecords";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import type { FileRecord, FileRecordVersion } from "@cohesive/api-client";
import { isAxiosError } from "axios";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import toast from "react-hot-toast";
import { FileRecordVersionPreviewPane } from "./FileRecordVersionPreviewPane";
import { useFileRecordVersionPreviewPaneContext } from "./FileRecordVersionPreviewPaneContext";
import { formatVersionHistoryDate } from "./formatVersionHistoryDate";
import { pickFileRecordVersionMediaURL } from "./pickFileRecordVersionMediaURL";
import {
  VersionHistoryDialog,
  VersionHistoryListItem,
} from "./VersionHistoryDialog";

export function fileRecordEligibleForApiVersioning(
  record: FileRecord,
): boolean {
  if (!record.id) return false;
  if (record.path.endsWith("/")) return false;
  if (record.status === "pending" || record.status === "deleted") return false;
  return true;
}

type FileRecordVersioningContextValue = {
  record: FileRecord;
  versions: FileRecordVersion[];
  versionsError: Error | null;
  isVersionsLoading: boolean;
  isHistoryOpen: boolean;
  toggleHistory: () => void;
  setIsHistoryOpen: (open: boolean) => void;
  onHistoryOpenChange: (open: boolean) => void;
  selectedVersionId: string | null;
  setSelectedVersionId: (id: string | null) => void;
  selectedVersion: FileRecordVersion | null;
  restoreMutation: ReturnType<typeof useRestoreFileRecordVersionMutation>;
  restoreDialogOpen: boolean;
  setRestoreDialogOpen: (open: boolean) => void;
  confirmRestore: () => void;
};

const FileRecordVersioningContext =
  createContext<FileRecordVersioningContextValue | null>(null);

export function useFileRecordVersioningOptional() {
  return useContext(FileRecordVersioningContext);
}

export function useFileRecordVersionPreview() {
  const previewPaneVersion = useFileRecordVersionPreviewPaneContext();
  const ctx = useFileRecordVersioningOptional();

  if (previewPaneVersion === undefined) {
    return {
      previewVersion: null as FileRecordVersion | null,
      previewMediaUrl: undefined as string | undefined,
      previewFetchUrl: undefined as string | undefined,
      isPreviewing: false,
    };
  }

  const v = previewPaneVersion;
  if (!v) {
    return {
      previewVersion: null,
      previewMediaUrl: undefined,
      previewFetchUrl: undefined,
      isPreviewing: false,
    };
  }

  const record = ctx?.record;
  const previewMediaUrl = record
    ? pickFileRecordVersionMediaURL(v, "full", record.content_type)
    : undefined;
  const previewFetchUrl = v.url ?? v.download_url;
  return {
    previewVersion: v,
    previewMediaUrl,
    previewFetchUrl,
    isPreviewing: true,
  };
}

export function FileRecordVersioningProvider({
  record,
  children,
  initialHistoryOpen = false,
  onHistoryClose,
}: {
  record: FileRecord;
  children: ReactNode;
  /** Open the history dialog immediately (used by the imperative callable). */
  initialHistoryOpen?: boolean;
  /** Fires once whenever the history dialog transitions to closed. */
  onHistoryClose?: () => void;
}) {
  const versionsQuery = useFileRecordVersionsQuery(record.id, {
    enabled: !!record.id,
  });
  const versions = versionsQuery.data ?? [];
  const [isHistoryOpen, setIsHistoryOpen] = useState(initialHistoryOpen);

  // Kept in a ref so the memoized close handlers don't churn on identity.
  const onHistoryCloseRef = useRef(onHistoryClose);
  onHistoryCloseRef.current = onHistoryClose;
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null,
  );
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);

  const selectedVersion = useMemo(
    () => versions.find((x) => x.id === selectedVersionId) ?? null,
    [versions, selectedVersionId],
  );

  const restoreMutation = useRestoreFileRecordVersionMutation({
    onSuccess: () => {
      toast.success("Version restored.");
      setRestoreDialogOpen(false);
      setIsHistoryOpen(false);
      setSelectedVersionId(null);
      onHistoryCloseRef.current?.();
    },
    onError: (err: unknown) => {
      let msg = "Restore failed.";
      if (isAxiosError(err)) {
        const data = err.response?.data as
          | { error?: { message?: string } }
          | undefined;
        if (data?.error?.message) msg = data.error.message;
        else if (err.message) msg = err.message;
      } else if (err instanceof Error) {
        msg = err.message;
      }
      toast.error(msg);
    },
  });

  const toggleHistory = useCallback(() => {
    setIsHistoryOpen((open) => {
      if (open) {
        setSelectedVersionId(null);
        onHistoryCloseRef.current?.();
        return false;
      }
      return true;
    });
  }, []);

  const handleHistoryOpenChange = useCallback((open: boolean) => {
    setIsHistoryOpen(open);
    if (!open) {
      setSelectedVersionId(null);
      onHistoryCloseRef.current?.();
    }
  }, []);

  useEffect(() => {
    if (!isHistoryOpen) return;
    if (selectedVersionId !== null) return;
    if (!versions.length) return;
    setSelectedVersionId(versions[0].id);
  }, [isHistoryOpen, selectedVersionId, versions]);

  const confirmRestore = useCallback(() => {
    if (!selectedVersion) return;
    restoreMutation.mutate({
      fileRecordId: record.id,
      fileRecordVersionId: selectedVersion.id,
    });
  }, [record.id, restoreMutation, selectedVersion]);

  const value: FileRecordVersioningContextValue = {
    record,
    versions,
    versionsError: versionsQuery.error as Error | null,
    isVersionsLoading: versionsQuery.isPending,
    isHistoryOpen,
    toggleHistory,
    setIsHistoryOpen,
    onHistoryOpenChange: handleHistoryOpenChange,
    selectedVersionId,
    setSelectedVersionId,
    selectedVersion,
    restoreMutation,
    restoreDialogOpen,
    setRestoreDialogOpen,
    confirmRestore,
  };

  return (
    <FileRecordVersioningContext.Provider value={value}>
      {children}
    </FileRecordVersioningContext.Provider>
  );
}

function formatVersionListLabel(v: FileRecordVersion): string {
  return formatVersionHistoryDate(v.version_created_at) || v.id.slice(0, 8);
}

function versionListSubtitle(
  _v: FileRecordVersion,
  index: number,
  total: number,
) {
  if (index === total - 1) return "Initial version";
  return `Version ${total - index}`;
}

export function FileRecordVersionHistoryDialog() {
  const ctx = useFileRecordVersioningOptional();
  if (!ctx) return null;

  const {
    isHistoryOpen,
    onHistoryOpenChange,
    record,
    versions,
    versionsError,
    isVersionsLoading,
    selectedVersionId,
    setSelectedVersionId,
    selectedVersion,
    setRestoreDialogOpen,
    restoreMutation,
  } = ctx;

  return (
    <VersionHistoryDialog
      open={isHistoryOpen}
      onOpenChange={onHistoryOpenChange}
      preview={
        <FileRecordVersionPreviewPane
          record={record}
          selectedVersion={selectedVersion}
        />
      }
      footer={
        <Button
          type="button"
          size="sm"
          disabled={!selectedVersion || restoreMutation.isPending}
          onClick={() => setRestoreDialogOpen(true)}
        >
          Restore
        </Button>
      }
    >
      {isVersionsLoading && (
        <p className="px-2 py-1 text-xs text-muted-foreground">
          Loading versions…
        </p>
      )}
      {versionsError && (
        <p className="px-2 py-1 text-xs text-destructive">
          {versionsError.message || "Failed to load versions."}
        </p>
      )}
      {!isVersionsLoading && !versionsError && versions.length === 0 && (
        <p className="px-2 py-1 text-xs text-muted-foreground">
          No older versions in the last 30 days.
        </p>
      )}
      {versions.map((v, i) => (
        <VersionHistoryListItem
          key={v.id}
          active={selectedVersionId === v.id}
          title={formatVersionListLabel(v)}
          subtitle={versionListSubtitle(v, i, versions.length)}
          onClick={() => setSelectedVersionId(v.id)}
        />
      ))}
    </VersionHistoryDialog>
  );
}

export function FileRecordVersionRestoreDialog() {
  const ctx = useFileRecordVersioningOptional();
  if (!ctx) return null;

  const {
    restoreDialogOpen,
    setRestoreDialogOpen,
    confirmRestore,
    restoreMutation,
    selectedVersion,
  } = ctx;

  return (
    <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Restore to this version?</AlertDialogTitle>
          <AlertDialogDescription>
            The file will be replaced with the version you are previewing
            {selectedVersion
              ? ` (${formatVersionListLabel(selectedVersion)}).`
              : "."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={restoreMutation.isPending}>
            Cancel
          </AlertDialogCancel>
          <Button
            type="button"
            disabled={restoreMutation.isPending}
            onClick={() => confirmRestore()}
          >
            Restore
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
